import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PlaylistApi } from './PlaylistApi';
import { PlaylistUtils } from './PlaylistUtils';
import { PLAYLIST_QUERY_KEYS, type PlaylistAddPosition } from '@/lib/constants/playlist';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { createBatchMutationHook } from '@/shared/hooks/useBatchMutation';
import type {
  CreateCustomPlaylistRequest, CreateTagPlaylistRequest,
  UpdateCustomPlaylistRequest, UpdateTagPlaylistRequest,
  CustomPlaylistView,
} from '@/lib/types/Playlist';

export function useCreateCustomPlaylist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: CreateCustomPlaylistRequest) =>
      PlaylistApi.createCustomPlaylist(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

export function useCreateTagPlaylist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: CreateTagPlaylistRequest) =>
      PlaylistApi.createTagPlaylist(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

export function useUpdateCustomPlaylist(listId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: UpdateCustomPlaylistRequest) =>
      PlaylistApi.updateCustomPlaylist(selectedCId!, listId, req),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        PLAYLIST_QUERY_KEYS.customDetail(selectedCId!, listId),
        updated
      );
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

export function useUpdateTagPlaylist(listId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: UpdateTagPlaylistRequest) =>
      PlaylistApi.updateTagPlaylist(selectedCId!, listId, req),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        PLAYLIST_QUERY_KEYS.tagDetail(selectedCId!, listId),
        updated
      );
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

export interface AddMediaToPlaylistInput {
  listId: number;
  mediaIds: number[];
  addPosition: PlaylistAddPosition;
}

export interface AddMediaToPlaylistResult {
  playlist: CustomPlaylistView;
  addedCount: number;
  skippedCount: number;
}

/**
 * Append media to a manual playlist from outside its edit page ("Add to
 * Playlist" on the Videos/Streams grids). The backend has no append call —
 * `list/custom/{id}` POST replaces every position row — so the base is
 * re-fetched immediately before the write; merging onto the (up to 2 min
 * stale) list cache could silently drop items added elsewhere. Ids already
 * in the playlist are skipped; when nothing is new the write is skipped too.
 */
export function useAddMediaToPlaylist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ listId, mediaIds, addPosition }: AddMediaToPlaylistInput): Promise<AddMediaToPlaylistResult> => {
      const cId = selectedCId!;
      const current = await PlaylistApi.getCustomPlaylist(cId, listId);
      const merge = PlaylistUtils.mergeMediaPositions(current.mediaPositions, mediaIds, addPosition);
      if (merge.addedCount === 0) {
        return { playlist: current, addedCount: 0, skippedCount: merge.skippedCount };
      }
      const request = PlaylistUtils.buildCustomUpdateRequest(
        PlaylistUtils.toSettingsFormData(current),
        merge.positions,
      );
      const updated = await PlaylistApi.updateCustomPlaylist(cId, listId, request);
      return { playlist: updated, addedCount: merge.addedCount, skippedCount: merge.skippedCount };
    },
    onSuccess: ({ playlist }) => {
      queryClient.setQueryData(
        PLAYLIST_QUERY_KEYS.customDetail(selectedCId!, playlist.listId),
        playlist
      );
      queryClient.invalidateQueries({ queryKey: PLAYLIST_QUERY_KEYS.all(selectedCId!) });
    },
  });
}

/** Activate playlists (batch). */
export const useActivatePlaylists = createBatchMutationHook(
  PlaylistApi.activatePlaylists,
  (cId) => PLAYLIST_QUERY_KEYS.all(cId),
);

/** Deactivate playlists (batch). */
export const useDeactivatePlaylists = createBatchMutationHook(
  PlaylistApi.deactivatePlaylists,
  (cId) => PLAYLIST_QUERY_KEYS.all(cId),
);

/** Delete playlists (batch). */
export const useDeletePlaylists = createBatchMutationHook(
  PlaylistApi.deletePlaylists,
  (cId) => PLAYLIST_QUERY_KEYS.all(cId),
);
